import {
  MessageTemplateDetail,
  MessageTemplatesService,
  TemplateRecipient,
} from "client/openapi";
import { errorText } from "components/Forms/formsApi";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  EmailFrame,
  PreviewNotes,
  SegmentCount,
  TextBubble,
  useTemplatePreview,
} from "./TemplatePreviewParts";
import { TemplateWording } from "./templatesApi";
import {
  FormatBar,
  VariablePicker,
  applyEdit,
  formatted,
  insertText,
} from "./WordingTools";
import { useCatalogVariables } from "./useCatalog";

/**
 * Recipients past the first the preview looks at: it names whose details
 * would be blank (for the first 100) and who unsubscribed.
 */
const OTHERS_CHECKED = 500;

/**
 * A template chosen in a message box: how it reads for the first person
 * it is for (the others get their own details; the preview names whose
 * would be blank), and its wording to change for this message only.
 * wording is undefined while the template's own is used.
 */
export default function TemplateMessage({
  orgId,
  templateId,
  recipients,
  sms,
  email,
  wording,
  onWording,
  disabled,
}: {
  orgId: number;
  templateId: number;
  recipients: TemplateRecipient[];
  sms: boolean;
  email: boolean;
  wording?: TemplateWording;
  onWording: (wording?: TemplateWording) => void;
  disabled?: boolean;
}) {
  const [template, setTemplate] = useState<MessageTemplateDetail>();
  const [loadError, setLoadError] = useState<string>();
  const variables = useCatalogVariables(orgId, wording !== undefined);
  const subjectRef = useRef<HTMLInputElement>(null);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const htmlRef = useRef<HTMLTextAreaElement>(null);
  const smsRef = useRef<HTMLTextAreaElement>(null);
  const [lastField, setLastField] = useState<"subject" | "body" | "sms">(
    "body"
  );

  useEffect(() => {
    setTemplate(undefined);
    MessageTemplatesService.getMessageTemplate({ templateId })
      .then(setTemplate)
      .catch((e) =>
        setLoadError(errorText(e, "The template could not be loaded."))
      );
  }, [templateId]);

  const request = useMemo(
    () => ({
      template_id: templateId,
      person: recipients[0] ?? null,
      others: recipients.slice(1, OTHERS_CHECKED + 1),
      ...wording,
    }),
    [templateId, recipients, wording]
  );
  const { preview, error } = useTemplatePreview(orgId, request);

  const set = (changes: TemplateWording) =>
    onWording({ ...wording, ...changes });
  // The template's own HTML is its email: that is what changes here.
  const ownHtml = wording?.email_format === "html";

  /** A variable typed in at the cursor of the box last used. */
  function pickVariable(key: string) {
    const text = `{{${key}}}`;
    if (!wording) return;
    if (lastField === "subject")
      applyEdit(
        subjectRef,
        insertText(subjectRef.current, wording.email_subject ?? "", text),
        (value) => set({ email_subject: value })
      );
    else if (lastField === "sms")
      applyEdit(
        smsRef,
        insertText(smsRef.current, wording.sms_body ?? "", text),
        (value) => set({ sms_body: value })
      );
    else if (ownHtml)
      applyEdit(
        htmlRef,
        insertText(htmlRef.current, wording.email_html ?? "", text),
        (value) => set({ email_html: value })
      );
    else
      applyEdit(
        bodyRef,
        insertText(bodyRef.current, wording.email_body ?? "", text),
        (value) => set({ email_body: value })
      );
  }

  if (loadError)
    return <p className="mb-3 text-sm text-red-600">{loadError}</p>;

  return (
    <div className="mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <p className="text-sm font-semibold text-gray-900">
        {!preview?.person
          ? "How it reads"
          : recipients.length === 0
          ? `How it reads, for example for ${preview.person.name}`
          : `How ${preview.person.name} gets it`}
      </p>
      <p className="text-xs text-gray-600">
        Each person gets it with their own details.
      </p>
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      {!preview ? (
        <div className="mt-2 h-24 animate-pulse rounded bg-gray-200" />
      ) : (
        <>
          {email && (
            <div className="mt-2">
              <p className="text-sm text-gray-700">
                <span className="font-semibold">Subject:</span>{" "}
                {preview.subject}
              </p>
              <div className="mt-1">
                <EmailFrame
                  html={preview.html}
                  title="Email preview"
                  height={260}
                />
              </div>
            </div>
          )}
          {sms && (
            <div className="mt-2">
              <TextBubble text={preview.sms} />
              <SegmentCount preview={preview} />
            </div>
          )}
          <PreviewNotes preview={preview} />
        </>
      )}

      {wording === undefined ? (
        <button
          type="button"
          className="mt-2 text-sm font-medium text-gray-700 underline disabled:opacity-50"
          disabled={!template || disabled}
          onClick={() =>
            template &&
            onWording({
              email_subject: template.email_subject ?? "",
              email_body: template.email_body ?? "",
              email_format:
                template.email_format === "html" ? "html" : "editor",
              email_html: template.email_html ?? "",
              sms_body: template.sms_body ?? "",
            })
          }
        >
          Change the wording for this message
        </button>
      ) : (
        <div className="mt-3 border-t border-gray-200 pt-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-semibold text-gray-900">
              Wording for this message only
            </p>
            {variables && (
              <VariablePicker
                groups={variables}
                onPick={pickVariable}
                disabled={disabled}
              />
            )}
          </div>
          {email && (
            <>
              <label
                htmlFor="template-subject"
                className="input-label mt-2 font-bold"
              >
                Email subject
              </label>
              <input
                id="template-subject"
                ref={subjectRef}
                className="input mt-0 rounded-lg"
                value={wording.email_subject ?? ""}
                maxLength={200}
                disabled={disabled}
                onFocus={() => setLastField("subject")}
                onChange={(e) => set({ email_subject: e.target.value })}
              />
              {ownHtml ? (
                <>
                  <label
                    htmlFor="template-html"
                    className="input-label mt-2 font-bold"
                  >
                    Email HTML
                  </label>
                  <textarea
                    id="template-html"
                    ref={htmlRef}
                    className="input mt-0 rounded-lg bg-white font-mono text-xs"
                    rows={8}
                    spellCheck={false}
                    value={wording.email_html ?? ""}
                    disabled={disabled}
                    onFocus={() => setLastField("body")}
                    onChange={(e) => set({ email_html: e.target.value })}
                  />
                </>
              ) : (
                <>
                  <label
                    htmlFor="template-body"
                    className="input-label mt-2 font-bold"
                  >
                    Email
                  </label>
                  <FormatBar
                    disabled={disabled}
                    onFormat={(format) =>
                      applyEdit(
                        bodyRef,
                        formatted(
                          bodyRef.current,
                          wording.email_body ?? "",
                          format
                        ),
                        (value) => set({ email_body: value })
                      )
                    }
                  />
                  <textarea
                    id="template-body"
                    ref={bodyRef}
                    className="input mt-1 rounded-lg bg-white"
                    rows={6}
                    value={wording.email_body ?? ""}
                    disabled={disabled}
                    onFocus={() => setLastField("body")}
                    onChange={(e) => set({ email_body: e.target.value })}
                  />
                </>
              )}
            </>
          )}
          {sms && (
            <>
              <label
                htmlFor="template-sms"
                className="input-label mt-2 font-bold"
              >
                Text
              </label>
              <textarea
                id="template-sms"
                ref={smsRef}
                className="input mt-0 rounded-lg bg-white"
                rows={3}
                maxLength={1600}
                value={wording.sms_body ?? ""}
                disabled={disabled}
                onFocus={() => setLastField("sms")}
                onChange={(e) => set({ sms_body: e.target.value })}
              />
            </>
          )}
          <button
            type="button"
            className="mt-2 text-sm font-medium text-gray-700 underline"
            disabled={disabled}
            onClick={() => onWording(undefined)}
          >
            Use the template&apos;s own wording
          </button>
        </div>
      )}
    </div>
  );
}
